// pages/BillingApprovalsPage.js
// Billing Team queue for staged Client Billing Review edits. A manager/partner's
// edit lands as a PENDING row in dataIntegrity.billing_config_changes; approving
// it here applies it to billing.* (or to PE, for delivery formats).
//   GET  /api/diBillingApproval?status=&section=&period=  -> { changes, pendingCount, progress }
//        (each change carries its client's office / originator / partner / manager, the requester's
//         name, the manager / partner sign-offs and, for billing-group moves, the group members —
//         see api/shared/billingConfig/approvalsView.js). A pending request only appears once the
//         client's manager has signed off.
//   POST /api/diBillingApproval { ids, decision } -> { results }
//
// One row per CLIENT on every tab; the chevron opens that client's requests (the ones in the tab).
// Deciding is optimistic: the rows leave / change status at once and come back if the server refuses.
// A write that fails on approval is not a separate status: the request stays pending, showing why.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import TopBar from '../components/TopBar';
import { BILLING_CHANGES_EVENT } from './sections/billingChangeApi';
import './BillingApprovalsPage.css';

// Small batches: an approval can mean Practice Engine writes (slow), and the server stops a batch
// at its time budget and hands back `remaining` ids, which we simply send again.
const MAX_IDS = 20;
const POLL_MS = 60000;
const PAGE_SIZE = 50;

const TABS = [
  { key: 'pending',  label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all',      label: 'All' },
];

const SECTION_LABELS = {
  billing_group:   'Billing group',
  client_standard: 'Client standard',
  recurring:       'Recurring bill',
  force_bill:      'Force bill',
  misc:            'Misc toggle',
  delivery_format: 'Delivery format',
  narrative:       'Narrative standard',
};

// Plain-language field names (keys are matched case-insensitively).
const FIELD_LABELS = {
  parentcode: 'Billing parent', value: 'Setting', service: 'Service', enabled: 'Turned on',
  billamount: 'Bill amount', billtype: 'Bill type', frequency: 'Frequency',
  narratives: 'Invoice narratives', narrative: 'Narrative wording', population: 'Applies to',
  level: 'Level', type: 'Type', serv: 'Service', idx: 'Jobs', applytogroup: 'Applies to whole billing group',
  lumpdisbursements: 'Lump disbursements into total', lumpwithparent: "Lump billing group children's WIP",
  applicablethrough: 'Applicable through', amount: 'Amount',
};

// Hide plumbing fields that carry no review value.
const HIDDEN_KEYS = new Set(['ContIndex']);
// A narrative standard's / recurring bill's GUID is plumbing, not something a reviewer reads.
const hiddenKeysFor = change => (change.section === 'narrative' || change.section === 'recurring'
  ? new Set([...HIDDEN_KEYS, 'uuid'])
  : HIDDEN_KEYS);

const uniqSorted = arr => [...new Set(arr.filter(Boolean))].sort((a, b) => String(a).localeCompare(String(b)));
const whoOf = (name, email) => name || email || '';

// The on/off settings' own titles (as on the Billing Review page) — used instead of a generic "Setting".
const TOGGLE_TITLES = {
  techFee:              'Tech Fee Narrative Line Broken Out',
  freebieReturn:        'Freebie Return',
  rollIndtaxIntoBustax: 'Roll INDTAX WIP into highest BUSTAX narrative line',
  discountToggle:       'Discount Toggle',
  excludeClient:        'Exclude from automated billing',
  gccEnforceThreshold:  'Enforce $500 WIP threshold',
  append_client_name:   'Append Client Name on Invoice',
};
const isMiscToggle = c => c.section === 'misc' || (c.section === 'billing_group' && c.fieldKey === 'append_client_name');
// The badge next to a request: its kind of change ("Misc toggle" for any on/off setting).
const chipLabel = c => (isMiscToggle(c) ? 'Misc toggle' : (SECTION_LABELS[c.section] || c.section));

// What the FIELD column calls a row: the setting's own name for toggles and delivery formats.
function rowLabel(k, change) {
  if (String(k).toLowerCase() === 'value') {
    if (isMiscToggle(change) && TOGGLE_TITLES[change.fieldKey]) return TOGGLE_TITLES[change.fieldKey];
    if (change.section === 'delivery_format') {
      return change.fieldKey === 'ClientInvDeliveryFormat' ? 'Invoice delivery' : 'Statement delivery';
    }
  }
  return fieldLabel(k);
}

function fieldLabel(k) {
  return FIELD_LABELS[String(k).toLowerCase()]
    || k.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase());
}

const money = n => {
  const x = Number(n);
  return Number.isFinite(x) ? x.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : String(n);
};
const titleCase = v => String(v).toLowerCase().replace(/(^|[\s_])([a-z])/g, (m, a, b) => (a ? ' ' : '') + b.toUpperCase());
const stripHtml = v => String(v).replace(/<br\s*\/?>/gi, ' / ').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
const LEVELS = { SERV: 'Service', JOB: 'Job' };
const TYPES = { TIME: 'Time', DISB: 'Disbursements', ALL: 'All' };

// A value as an end user would say it — no JSON, no raw codes.
function fmtVal(key, v, change) {
  if (v === null || v === undefined || v === '') return '—';
  const lk = String(key).toLowerCase();

  if (change.section === 'delivery_format' && lk === 'value') return v === 1 ? 'Email' : v === 0 ? 'Print and Mail' : String(v);
  if (change.section === 'narrative' && lk === 'narrative') return stripHtml(v) || '—';
  if (change.section === 'client_standard' && lk === 'value' && Number.isFinite(Number(v))) return `${Number(v)}%`;

  if (typeof v === 'boolean') return lk === 'value' || lk === 'enabled' ? (v ? 'On' : 'Off') : (v ? 'Yes' : 'No');
  if (lk === 'billamount' || lk === 'amount') return money(v);
  if (lk === 'applicablethrough') {
    const d = new Date(`${String(v).slice(0, 10)}T00:00:00`);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleDateString('en-US');
  }
  if (lk === 'level') return LEVELS[String(v).toUpperCase()] || titleCase(v);
  if (lk === 'type' && change.section === 'narrative') return TYPES[String(v).toUpperCase()] || titleCase(v);
  if (lk === 'frequency' || lk === 'billtype') return titleCase(v);

  if (Array.isArray(v)) {
    if (!v.length) return '—';
    // Invoice narrative lines: "1. wording — $amount" (the amount only matters once a bill has 2+ lines).
    if (lk === 'narratives' && typeof v[0] === 'object') {
      const lines = [...v].sort((a, b) => (Number(a.lineOrder) || 0) - (Number(b.lineOrder) || 0));
      return lines.map((n, i) => `${i + 1}. ${stripHtml(n.narrative ?? '')}${lines.length > 1 && n.amount != null ? ` — ${money(n.amount)}` : ''}`).join('\n');
    }
    // Job template numbers -> job names when the server could look them up.
    if (v.every(x => Number.isFinite(Number(x)) && String(x).trim() !== '')) {
      const names = v.map(x => (change.jobNames && change.jobNames[x]) || null);
      if (names.every(Boolean)) return names.join('\n');
      return `${v.length} job${v.length === 1 ? '' : 's'}`;
    }
    return v.map(x => (typeof x === 'object' ? fmtVal('', x, change) : String(x))).join(', ');
  }
  if (typeof v === 'object') {
    return Object.entries(v).map(([k, x]) => `${fieldLabel(k)}: ${fmtVal(k, x, change)}`).join('; ');
  }
  return String(v);
}

function relTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function fullTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

async function readError(res) {
  const text = await res.text().catch(() => '');
  try { const j = JSON.parse(text); return j.error || text; } catch { return text || `HTTP ${res.status}`; }
}

// Manager / partner sign-off state for a client. A partner who hasn't reviewed yet is flagged.
function SignedOff({ signoff }) {
  const m = signoff && signoff.manager;
  const p = signoff && signoff.partner;
  const tip = (label, s) => (s ? `${label}: ${whoOf(s.byName, s.by)} — ${fullTime(s.at)}` : `${label} hasn't reviewed`);
  return (
    <span className="ba-so">
      <span className={`ba-so-chip ${m ? 'ba-so-chip--yes' : 'ba-so-chip--no'}`} title={tip('Manager', m)}>
        <i className={`fas ${m ? 'fa-check' : 'fa-minus'}`} /> Mgr{m ? ` · ${whoOf(m.byName, m.by)}` : ''}
      </span>
      <span className={`ba-so-chip ${p ? 'ba-so-chip--yes' : 'ba-so-chip--flag'}`} title={tip('Partner', p)}>
        <i className={`fas ${p ? 'fa-check' : 'fa-flag'}`} /> {p ? `Ptr · ${whoOf(p.byName, p.by)}` : 'Partner not reviewed'}
      </span>
    </span>
  );
}

// One side of a billing-group move: the parent and who is in its group.
function GroupSide({ title, group }) {
  return (
    <div className="ba-group-side">
      <div className="ba-group-title">{title}</div>
      {!group ? <div className="ba-group-none">Not in a billing group (bills on its own)</div> : (
        <>
          <div className="ba-group-parent">
            <strong>{group.code}</strong>{group.name ? ` — ${group.name}` : ''} <span className="ba-group-tag">parent</span>
          </div>
          {group.members.length === 0
            ? <div className="ba-group-none">No other members</div>
            : (
              <ul className="ba-group-members">
                {group.members.map(m => <li key={m.code}><strong>{m.code}</strong>{m.name ? ` — ${m.name}` : ''}</li>)}
              </ul>
            )}
          <div className="ba-group-count">{group.members.length} member{group.members.length === 1 ? '' : 's'}</div>
        </>
      )}
    </div>
  );
}

function DetailsTable({ change }) {
  const before = change.before && typeof change.before === 'object' ? change.before : null;
  const payload = change.payload && typeof change.payload === 'object' ? change.payload : {};
  const hidden = hiddenKeysFor(change);
  // Recurring changes recorded the live value camelCase (billAmount) and the request PascalCase
  // (BillAmount): match keys case-insensitively so each field is ONE row, current beside requested.
  const lcMap = obj => Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [k.toLowerCase(), v]));
  const beforeLc = lcMap(before);
  const payloadLc = lcMap(payload);
  const labelKey = {};                                   // lowercase key -> a spelling to label it by
  for (const k of [...Object.keys(before || {}), ...Object.keys(payload)]) labelKey[k.toLowerCase()] = k;
  for (const k of Object.keys(payload)) labelKey[k.toLowerCase()] = k;    // prefer the request's spelling
  const keys = [...new Set([...Object.keys(payloadLc), ...(before ? Object.keys(beforeLc) : [])])]
    .filter(k => !hidden.has(labelKey[k]));
  return (
    <div className="ba-details">
      {keys.length > 0 && (
        <table className="ba-diff">
          <thead>
            <tr><th>Field</th><th>Current</th><th>Requested</th></tr>
          </thead>
          <tbody>
            {keys.map(lk => {
              const k = labelKey[lk];
              const cur = before ? beforeLc[lk] : undefined;
              const req = change.op === 'delete' ? undefined : payloadLc[lk];
              const changed = JSON.stringify(cur) !== JSON.stringify(req);
              return (
                <tr key={lk} className={changed ? 'ba-diff-changed' : ''}>
                  <td>{rowLabel(k, change)}</td>
                  <td>{fmtVal(k, cur, change)}</td>
                  <td>{change.op === 'delete' ? <em>removed</em> : fmtVal(k, req, change)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {change.groupInfo && (
        <div className="ba-group">
          <GroupSide title="Current group" group={change.groupInfo.current} />
          <GroupSide title="Requested group" group={change.groupInfo.requested} />
        </div>
      )}
      <details className="ba-raw">
        <summary>Raw JSON</summary>
        <pre>{JSON.stringify({ before: change.before, payload: change.payload }, null, 2)}</pre>
      </details>
    </div>
  );
}

// One request inside a client's drill-down.
function ChangeRow({ change, selected, onToggle, onApprove, onReject }) {
  // Opens this request's Current vs Requested table. Local state keyed by the row, so the 60 s poll
  // (which only swaps data) never collapses it.
  const [open, setOpen] = useState(false);
  const pending = change.status === 'pending';
  const isPE = change.section === 'delivery_format';
  return (
    <div className={`ba-row ba-row--${change.status}`}>
      <div className="ba-row-main">
        <button
          type="button"
          className="ba-expand-btn ba-expand-btn--change"
          aria-expanded={open}
          aria-label={`${open ? 'Hide' : 'Show'} details for change ${change.changeId}`}
          onClick={() => setOpen(o => !o)}
        >
          <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} />
        </button>
        {pending && (
          <input
            type="checkbox"
            className="ba-check"
            checked={selected}
            onChange={() => onToggle(change.changeId)}
            aria-label={`Select change ${change.changeId}`}
          />
        )}
        <div className="ba-row-body">
          <div className="ba-row-top">
            <span className="ba-chip">{chipLabel(change)}</span>
            <span className="ba-summary">{change.summary}</span>
            {isPE && pending && (
              <span className="ba-pe-cue" title="Approving this writes the value to Practice Engine">
                <i className="fas fa-exclamation-triangle" /> Writes to Practice Engine on approval
              </span>
            )}
            {!pending && <span className={`ba-status ba-status--${change.status}`}>{change.status}</span>}
          </div>
          <div className="ba-row-meta">
            <span title={`${change.requestedBy} — ${fullTime(change.requestedAt)}`}>
              <i className="fas fa-user" /> {whoOf(change.requestedByName, change.requestedBy)} · {relTime(change.requestedAt)}
            </span>
          </div>
          {!pending && (
            <div className="ba-row-decision">
              {change.decidedBy && (
                <span title={`${change.decidedBy} — ${fullTime(change.decidedAt)}`}>
                  <i className="fas fa-gavel" /> {whoOf(change.decidedByName, change.decidedBy)} · {relTime(change.decidedAt)}
                </span>
              )}
              {change.decisionNote && <span className="ba-note">“{change.decisionNote}”</span>}
              {change.appliedAt && <span title={fullTime(change.appliedAt)}><i className="fas fa-check" /> Applied {relTime(change.appliedAt)}</span>}
            </div>
          )}
          {change.applyError && (
            <div className="ba-apply-error"><i className="fas fa-times-circle" /> {change.applyError}</div>
          )}
          {open && <DetailsTable change={change} />}
        </div>
        {pending && (
          <div className="ba-row-actions">
            <button type="button" className="ba-btn ba-btn--approve" onClick={() => onApprove(change)}>
              <i className="fas fa-check" /> Approve
            </button>
            <button type="button" className="ba-btn ba-btn--reject" onClick={() => onReject(change)}>
              <i className="fas fa-times" /> Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// The queue itself (no TopBar), so it can also stand in for the Client Billing Review section page.
// `actions` is extra header content (the admin's Manager / Partner / Approvals switch).
export function BillingApprovals({ actions = null }) {
  const navigate = useNavigate();
  const { period } = useParams();
  const [tab, setTab] = useState('pending');
  const [section, setSection] = useState('');
  const [search, setSearch] = useState('');
  const [officeF, setOfficeF] = useState('');
  const [originatorF, setOriginatorF] = useState('');
  const [partnerF, setPartnerF] = useState('');
  const [managerF, setManagerF] = useState('');
  const [partnerRevF, setPartnerRevF] = useState('');   // '' | 'reviewed' | 'not-reviewed'
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());
  const [changes, setChanges] = useState(null);       // null until the first load lands
  const [pendingCount, setPendingCount] = useState(0);
  const [progress, setProgress] = useState(null);     // { pending, total } for this section's period
  const [error, setError] = useState(null);
  const [deciding, setDeciding] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  const reqSeq = useRef(0);
  const decidingRef = useRef(0);                  // decisions still on the wire (the poll waits for them)
  const changesRef = useRef(null);                // latest list, for snapshots taken inside callbacks

  const load = useCallback(async () => {
    const seq = ++reqSeq.current;
    try {
      const qs = new URLSearchParams({ status: tab });
      if (section) qs.set('section', section);
      if (period) qs.set('period', period);
      const res = await fetch(`/api/diBillingApproval?${qs.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error(await readError(res));
      const body = await res.json();
      if (seq !== reqSeq.current) return;              // a newer request superseded this one
      setChanges(body.changes || []);
      changesRef.current = body.changes || [];
      setPendingCount(Number(body.pendingCount) || 0);
      setProgress(body.progress || null);
      setError(null);
    } catch (err) {
      if (seq === reqSeq.current) setError(err.message || 'Failed to load approvals.');
    }
  }, [tab, section, period]);

  // Refetch on tab/section change, then poll while the tab is visible.
  useEffect(() => {
    load();
    const tick = () => { if (!document.hidden && !decidingRef.current) load(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    // This tab submitted / withdrew a change (e.g. an admin flipping between the two views).
    const onChanges = e => { if (!(e && e.detail && typeof e.detail.pendingDelta === 'number')) tick(); };
    window.addEventListener(BILLING_CHANGES_EVENT, onChanges);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener(BILLING_CHANGES_EVENT, onChanges);
    };
  }, [load]);

  // Selection / paging only make sense within the current view.
  useEffect(() => { setSelected(new Set()); setPage(1); }, [tab, section]);

  // Drop selected ids that left the pending list (decided elsewhere / refreshed).
  useEffect(() => {
    if (!changes) return;
    setSelected(prev => {
      if (!prev.size) return prev;
      const live = new Set(changes.filter(c => c.status === 'pending').map(c => c.changeId));
      const next = new Set([...prev].filter(id => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [changes]);

  // Filter choices come from what's loaded in this tab.
  const options = useMemo(() => {
    const list = changes || [];
    return {
      offices: uniqSorted(list.map(c => c.office)),
      originators: uniqSorted(list.map(c => c.originator)),
      partners: uniqSorted(list.map(c => c.partner)),
      managers: uniqSorted(list.map(c => c.manager)),
    };
  }, [changes]);

  const visible = useMemo(() => {
    if (!changes) return [];
    const q = search.trim().toLowerCase();
    return changes.filter(c =>
      (!officeF || c.office === officeF) &&
      (!originatorF || c.originator === originatorF) &&
      (!partnerF || c.partner === partnerF) &&
      (!managerF || c.manager === managerF) &&
      (!partnerRevF || (partnerRevF === 'reviewed') === !!(c.signoff && c.signoff.partner)) &&
      (!q ||
        String(c.clientCode || '').toLowerCase().includes(q) ||
        String(c.clientName || '').toLowerCase().includes(q) ||
        String(c.summary || '').toLowerCase().includes(q) ||
        String(c.requestedBy || '').toLowerCase().includes(q) ||
        String(c.requestedByName || '').toLowerCase().includes(q)));
  }, [changes, search, officeF, originatorF, partnerF, managerF, partnerRevF]);

  // One row per client, with its requests underneath.
  const clientRows = useMemo(() => {
    const map = new Map();
    for (const c of visible) {
      const key = c.contIndex ?? c.clientCode;
      let row = map.get(key);
      if (!row) {
        row = {
          key, clientCode: c.clientCode, clientName: c.clientName, office: c.office,
          originator: c.originator, partner: c.partner, manager: c.manager, signoff: c.signoff, items: [], latest: 0,
        };
        map.set(key, row);
      }
      row.items.push(c);
      row.latest = Math.max(row.latest, new Date(c.requestedAt).getTime() || 0);
    }
    const rows = [...map.values()];
    rows.sort(tab === 'pending'
      ? (a, b) => String(a.clientCode).localeCompare(String(b.clientCode))
      : (a, b) => b.latest - a.latest);
    return rows;
  }, [visible, tab]);

  const totalPages = Math.max(1, Math.ceil(clientRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = clientRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const visiblePendingIds = useMemo(
    () => visible.filter(c => c.status === 'pending').map(c => c.changeId), [visible]);
  const allSelected = visiblePendingIds.length > 0 && visiblePendingIds.every(id => selected.has(id));

  const toggle = useCallback(id => {
    setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(visiblePendingIds));
  };
  const pendingIdsOf = row => row.items.filter(c => c.status === 'pending').map(c => c.changeId);
  const toggleClient = row => {
    const ids = pendingIdsOf(row);
    const every = ids.every(id => selected.has(id));
    setSelected(prev => { const n = new Set(prev); ids.forEach(id => (every ? n.delete(id) : n.add(id))); return n; });
  };
  const toggleOpen = key => setExpanded(prev => {
    const n = new Set(prev);
    if (n.has(key)) n.delete(key); else n.add(key);
    return n;
  });

  // Optimistic: the decided changes leave the list (or flip status on the other tabs) and the pending
  // count / progress drop the moment you click; the server's answer then confirms them, and any that it
  // refuses (already decided by someone else, apply failed, request error) are put back.
  const decide = useCallback(async (ids, decision, note) => {
    if (!ids.length) return;
    const idSet = new Set(ids);
    const originals = (changesRef.current || []).filter(c => idSet.has(c.changeId) && c.status === 'pending');
    const origById = new Map(originals.map(c => [c.changeId, c]));
    const inPeriod = list => list.filter(c => period && c.period === period).length;
    const status = decision === 'approve' ? 'approved' : 'rejected';
    const patch = list => (list || []).map(c => (idSet.has(c.changeId) && c.status === 'pending'
      ? { ...c, status, decidedAt: new Date().toISOString() }
      : c));
    const apply = fn => setChanges(prev => { const next = prev ? fn(prev) : prev; changesRef.current = next; return next; });
    const restore = list => {
      if (!list.length) return;
      apply(prev => {
        const byId = new Map(list.map(c => [c.changeId, c]));
        const next = prev.map(c => (byId.has(c.changeId) ? byId.get(c.changeId) : c));
        const present = new Set(prev.map(c => c.changeId));
        for (const c of list) if (!present.has(c.changeId)) next.push(c);
        return next;
      });
      setPendingCount(n => n + list.length);
      setProgress(p => (p ? { ...p, pending: p.pending + inPeriod(list) } : p));
      window.dispatchEvent(new CustomEvent(BILLING_CHANGES_EVENT, { detail: { pendingDelta: list.length } }));
    };

    apply(prev => (tab === 'pending' ? prev.filter(c => !idSet.has(c.changeId)) : patch(prev)));
    setPendingCount(n => Math.max(0, n - originals.length));
    setProgress(p => (p ? { ...p, pending: Math.max(0, p.pending - inPeriod(originals)) } : p));
    window.dispatchEvent(new CustomEvent(BILLING_CHANGES_EVENT, { detail: { pendingDelta: -originals.length } }));
    setSelected(new Set());
    decidingRef.current += 1;
    setDeciding(true);

    let ok = 0;
    try {
      const queue = [...ids];
      while (queue.length) {
        const chunk = queue.splice(0, MAX_IDS);
        let res;
        try {
          res = await fetch('/api/diBillingApproval', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: chunk, decision, ...(note ? { note } : {}) }),
          });
        } catch (err) {
          toast.error(err.message || 'Request failed.', { autoClose: 10000 });
          restore(chunk.map(id => origById.get(id)).filter(Boolean));
          continue;
        }
        if (!res.ok) {
          toast.error(await readError(res), { autoClose: 10000 });
          restore(chunk.map(id => origById.get(id)).filter(Boolean));
          continue;
        }
        const body = await res.json();
        if (Array.isArray(body.remaining) && body.remaining.length) queue.unshift(...body.remaining);
        for (const r of body.results || []) {
          if (r.status === 'approved' || r.status === 'rejected') {
            ok++;
            // Approved: billing.* has the new value. Only the legacy blob mirror (botfield's billing run) missed it.
            if (r.mirrorWarning) toast.warn(`Change #${r.changeId} was approved and saved, but ${r.mirrorWarning.charAt(0).toLowerCase()}${r.mirrorWarning.slice(1)}`, { autoClose: 12000 });
          } else if (r.status === 'failed') {
            // The write failed: the request stays pending (the row persists) with the reason shown on it.
            toast.error(`Change #${r.changeId}: ${r.error || 'Apply failed.'}`, { autoClose: 8000 });
            const orig = origById.get(r.changeId);
            if (orig) restore([{ ...orig, applyError: r.error || 'Apply failed.' }]);
          } else {
            toast.error(`Change #${r.changeId}: ${r.error || 'Could not be decided.'}`, { autoClose: 8000 });
            restore([origById.get(r.changeId)].filter(Boolean));
          }
        }
      }
      if (ok) toast.success(`${ok} change${ok === 1 ? '' : 's'} ${decision === 'approve' ? 'approved' : 'rejected'}.`);
    } finally {
      decidingRef.current -= 1;
      if (decidingRef.current <= 0) { decidingRef.current = 0; setDeciding(false); }
      window.dispatchEvent(new Event(BILLING_CHANGES_EVENT));   // let other views recount on the real numbers
      load();
    }
  }, [load, tab, period]);

  // Approving anything that writes to Practice Engine (delivery formats) asks first, however it is
  // triggered (one request, one client's requests, or a multi-select).
  const confirmApprove = ids => {
    const idSet = new Set(ids);
    const pe = (changes || []).filter(c => idSet.has(c.changeId) && c.section === 'delivery_format').length;
    const msg = `Approve ${ids.length} change${ids.length === 1 ? '' : 's'}?` +
      (pe ? `\n\n${pe} will write to Practice Engine.` : '');
    return window.confirm(msg);
  };
  const approveOne = c => {
    // A plain (non-PE) single approval stays one click; only PE writes are confirmed.
    if (c.section === 'delivery_format' && !confirmApprove([c.changeId])) return;
    decide([c.changeId], 'approve');
  };
  // A rejection can carry a reason (shown on the row as the decision note, so the requester can read why).
  // Cancel the prompt to cancel the rejection; leave it empty to reject without a reason.
  const askReason = () => {
    const r = window.prompt('Reason for rejecting (optional — the requester will see it):', '');
    return r === null ? null : r.trim().slice(0, 500);
  };
  const rejectWith = ids => { const note = askReason(); if (note !== null) decide(ids, 'reject', note); };
  const rejectOne = c => rejectWith([c.changeId]);
  const approveClient = row => {
    const ids = pendingIdsOf(row);
    if (row.items.some(c => c.status === 'pending' && c.section === 'delivery_format') && !confirmApprove(ids)) return;
    decide(ids, 'approve');
  };
  const rejectClient = row => rejectWith(pendingIdsOf(row));
  const approveSelected = () => {
    const ids = [...selected];
    if (confirmApprove(ids)) decide(ids, 'approve');
  };
  const rejectSelected = () => rejectWith([...selected]);

  const filtersOn = !!(search || section || officeF || originatorF || partnerF || managerF || partnerRevF);
  const clearFilters = () => {
    setSearch(''); setSection(''); setOfficeF(''); setOriginatorF(''); setPartnerF(''); setManagerF(''); setPartnerRevF(''); setPage(1);
  };
  const initialLoading = changes === null && !error;
  const emptyMsg = tab === 'pending' && !filtersOn ? 'Nothing waiting for approval.' : 'No changes match.';
  const pending = tab === 'pending';
  const colSpan = pending ? 10 : 9;

  const done = progress ? Math.max(0, progress.total - progress.pending) : 0;
  const pct = progress && progress.total > 0 ? Math.round((done / progress.total) * 100) : 0;

  return (
    <div className="ba-page">
      <div className="ba-header">
        <button className="back-btn ba-back" onClick={() => navigate('/')}>
          <i className="fas fa-arrow-left" /> Dashboard
        </button>
        <h2 className="ba-title"><i className="fas fa-clipboard-check" /> Billing Approvals</h2>
        {actions}
      </div>

      {progress && (
        <div className="ba-progress">
          <div className="ba-progress-head">
            <h3 className="ba-progress-title">Your Progress</h3>
            <span className="ba-progress-count">
              <strong>{progress.pending}</strong> pending / <strong>{progress.total}</strong> total requests
              <span className="ba-progress-pct">— {pct}% decided</span>
            </span>
          </div>
          <div className="ba-progress-track">
            <div
              className="ba-progress-fill"
              style={{ width: `${pct}%`, background: pct >= 100 ? '#1a7a4a' : pct >= 50 ? '#e6a817' : '#c0392b' }}
            />
          </div>
        </div>
      )}

      <div className="ba-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            type="button"
            className={`ba-tab${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'pending' && pendingCount > 0 && <span className="ba-tab-count">{pendingCount}</span>}
          </button>
        ))}
      </div>

      <div className="ba-filters">
        <input
          type="search"
          className="ba-search"
          placeholder="Search client, summary, requester…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
        />
        <select className="ba-select" value={officeF} onChange={e => { setOfficeF(e.target.value); setPage(1); }} aria-label="Office">
          <option value="">All offices</option>
          {options.offices.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="ba-select" value={originatorF} onChange={e => { setOriginatorF(e.target.value); setPage(1); }} aria-label="Originator">
          <option value="">All originators</option>
          {options.originators.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="ba-select" value={partnerF} onChange={e => { setPartnerF(e.target.value); setPage(1); }} aria-label="Partner">
          <option value="">All partners</option>
          {options.partners.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="ba-select" value={managerF} onChange={e => { setManagerF(e.target.value); setPage(1); }} aria-label="Manager">
          <option value="">All managers</option>
          {options.managers.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        <select className="ba-select" value={partnerRevF} onChange={e => { setPartnerRevF(e.target.value); setPage(1); }} aria-label="Partner review">
          <option value="">Partner review: any</option>
          <option value="not-reviewed">Partner not reviewed</option>
          <option value="reviewed">Partner reviewed</option>
        </select>
        <select className="ba-select" value={section} onChange={e => setSection(e.target.value)} aria-label="Change type">
          <option value="">All change types</option>
          {Object.entries(SECTION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        {filtersOn && (
          <button type="button" className="ba-clear" onClick={clearFilters}>Clear</button>
        )}

        {/* Bulk actions share the filter row (right side) instead of taking a row of their own. */}
        {pending && visiblePendingIds.length > 0 && (
          <div className="ba-bulk">
            <label
              className="ba-bulk-all"
              title={`${visiblePendingIds.length} request${visiblePendingIds.length === 1 ? '' : 's'} across ${clientRows.length} client${clientRows.length === 1 ? '' : 's'}`}
            >
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              Select all shown ({visiblePendingIds.length})
            </label>
            <span className="ba-bulk-sel">{selected.size} selected</span>
            <button type="button" className="ba-btn ba-btn--approve" disabled={!selected.size} onClick={approveSelected}>
              <i className="fas fa-check" /> Approve selected
            </button>
            <button type="button" className="ba-btn ba-btn--reject" disabled={!selected.size} onClick={rejectSelected}>
              <i className="fas fa-times" /> Reject selected
            </button>
            {deciding && <i className="fas fa-spinner fa-spin ba-working" title="Saving…" />}
          </div>
        )}
      </div>

      {error && !changes && (
        <div className="ba-error">
          <i className="fas fa-exclamation-triangle" /> {error}
          <button type="button" className="ba-btn" onClick={load}>Retry</button>
        </div>
      )}
      {error && changes && (
        <div className="ba-error ba-error--inline">
          <i className="fas fa-exclamation-triangle" /> Couldn't refresh: {error}
        </div>
      )}

      {initialLoading ? (
        <div className="ba-loading"><i className="fas fa-spinner fa-spin" /> Loading approvals…</div>
      ) : changes && clientRows.length === 0 ? (
        <div className="ba-empty"><i className="fas fa-inbox" /> {emptyMsg}</div>
      ) : changes ? (
        <>
          <div className="ba-table-wrap">
            <table className="ba-table">
              <thead>
                <tr>
                  <th className="ba-col-expand" />
                  {pending && <th className="ba-col-check" />}
                  <th>Client</th>
                  <th>Office</th>
                  <th>Originator</th>
                  <th>Partner</th>
                  <th>Manager</th>
                  <th className="ba-col-num">{pending ? 'Pending' : 'Requests'}</th>
                  <th>Signed off</th>
                  <th>{pending ? 'Latest request' : 'Latest activity'}</th>
                  {pending && <th className="ba-col-actions">Actions</th>}
                </tr>
              </thead>
              {pageRows.map(row => {
                const open = expanded.has(row.key);
                const ids = pendingIdsOf(row);
                const allIn = ids.length > 0 && ids.every(id => selected.has(id));
                return (
                  <tbody key={row.key} className="ba-client-group">
                    <tr className={`ba-client-row${open ? ' open' : ''}`} onClick={() => toggleOpen(row.key)}>
                      <td className="ba-col-expand">
                        <button
                          type="button"
                          className="ba-expand-btn"
                          aria-expanded={open}
                          aria-label={`${open ? 'Hide' : 'Show'} requests for ${row.clientCode}`}
                          onClick={e => { e.stopPropagation(); toggleOpen(row.key); }}
                        >
                          <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} />
                        </button>
                      </td>
                      {pending && (
                        <td className="ba-col-check" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={allIn}
                            onChange={() => toggleClient(row)}
                            aria-label={`Select all requests for ${row.clientCode}`}
                          />
                        </td>
                      )}
                      <td className="ba-cell-client">
                        <span className="ba-client-code">{row.clientCode}</span>
                        {row.clientName && <span className="ba-client-name">{row.clientName}</span>}
                      </td>
                      <td>{row.office || '—'}</td>
                      <td>{row.originator || '—'}</td>
                      <td>{row.partner || '—'}</td>
                      <td>{row.manager || '—'}</td>
                      <td className="ba-col-num"><span className="ba-count-badge">{row.items.length}</span></td>
                      <td><SignedOff signoff={row.signoff} /></td>
                      <td title={fullTime(new Date(row.latest).toISOString())}>{relTime(new Date(row.latest).toISOString())}</td>
                      {pending && (
                        <td className="ba-col-actions" onClick={e => e.stopPropagation()}>
                          <button type="button" className="ba-btn ba-btn--approve" onClick={() => approveClient(row)}>
                            <i className="fas fa-check" /> Approve{ids.length > 1 ? ` all (${ids.length})` : ''}
                          </button>
                          <button type="button" className="ba-btn ba-btn--reject" onClick={() => rejectClient(row)}>
                            <i className="fas fa-times" /> Reject{ids.length > 1 ? ' all' : ''}
                          </button>
                        </td>
                      )}
                    </tr>
                    {open && (
                      <tr className="ba-detail-row">
                        <td colSpan={colSpan}>
                          <div className="ba-detail-list">
                            {row.items.map(c => (
                              <ChangeRow
                                key={c.changeId}
                                change={c}
                                selected={selected.has(c.changeId)}
                                onToggle={toggle}
                                onApprove={approveOne}
                                onReject={rejectOne}
                              />
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                );
              })}
            </table>
          </div>

          {clientRows.length > PAGE_SIZE && (
            <div className="ba-pagination">
              <button type="button" disabled={safePage <= 1} onClick={() => setPage(1)} aria-label="First page"><i className="fas fa-angle-double-left" /></button>
              <button type="button" disabled={safePage <= 1} onClick={() => setPage(p => p - 1)} aria-label="Previous page"><i className="fas fa-angle-left" /></button>
              <span className="ba-page-info">Page {safePage} of {totalPages} <span className="ba-page-total">({clientRows.length.toLocaleString()} clients)</span></span>
              <button type="button" disabled={safePage >= totalPages} onClick={() => setPage(p => p + 1)} aria-label="Next page"><i className="fas fa-angle-right" /></button>
              <button type="button" disabled={safePage >= totalPages} onClick={() => setPage(totalPages)} aria-label="Last page"><i className="fas fa-angle-double-right" /></button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default function BillingApprovalsPage() {
  return (
    <>
      <TopBar />
      <BillingApprovals />
    </>
  );
}
